import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router";
import { supabase } from "../../lib/supabaseClient";
import { useAppSelector, useAppDispatch } from "../../hooks";
import { setNickname } from "../../features/nickname/nicknameSlice";
import { Film } from "../../types";
import sendIcon from "../../assets/chat-send.svg";
import CheckIcon from "../../assets/check.svg";
import CurrentIcon from "../../assets/current-location.svg";
import FavoriteIcon from "../../assets/favorite.svg";
import PlaylistIcon from "../../assets/playlist.svg";
import FilmModal from "../../components/Modals/FilmModal/FilmModal";
import CreateWatchPartyModal from "../../components/WatchParty/CreateWatchPartyModal";
import LoginRequiredModal from "../../components/Modals/LoginRequiredModal/LoginRequiredModal";
import WatchPartiesModal from "../../components/Modals/WatchPartiesModal/WatchPartiesModal";
import ChatMessage from "../../components/ChatMessage/ChatMessage";

type Message = {
  id?: string;
  content: string;
  nickname?: string | null;
  user_id?: string | null;
  created_at: string;
};

const generateUserId = () => `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const ChatRoom: React.FC = () => {
  const { filmId } = useParams<{ filmId: string }>();
  const navigate = useNavigate();
  const [film, setFilm] = useState<Film | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [usersCount, setUsersCount] = useState(0);
  const [channel, setChannel] = useState<any>(null);
  const [showFilmModal, setShowFilmModal] = useState(false);
  const { nickname } = useAppSelector((s: any) => s.nickname);
  const authUser = useAppSelector((s: any) => s.auth.user);
  const dispatch = useAppDispatch();
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [showCreatePartyModal, setShowCreatePartyModal] = useState(false);
  const [filmWatchParties, setFilmWatchParties] = useState<any[]>([]);
  const [isCurrentlyWatching, setIsCurrentlyWatching] = useState(false);
  const [showWatchPartiesModal, setShowWatchPartiesModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Add state for interactions
  const [userInteractions, setUserInteractions] = useState<{
    watched: boolean;
    favorite: boolean;
    will_watch: boolean;
  }>({
    watched: false,
    favorite: false,
    will_watch: false,
  });
  const [interactionLoading, setInteractionLoading] = useState(false);

  // Fetch film details by imdb_id
  useEffect(() => {
    if (!filmId) {
      setError("No film ID provided");
      setLoading(false);
      return;
    }

    const fetchFilm = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await supabase.from("films").select("*").eq("imdb_id", filmId).maybeSingle();

        if (fetchError) {
          // console.error("Error fetching film:", fetchError);
          setError(`Database error: ${fetchError.message}`);
          setLoading(false);
          return;
        }

        if (!data) {
          // console.error("No film found with IMDB ID:", filmId);
          setError("Film not found in database");
          setLoading(false);
          return;
        }

        setFilm(data);
        setLoading(false);
      } catch (err) {
        // console.error("Unexpected error:", err);
        setError("An unexpected error occurred");
        setLoading(false);
      }
    };

    fetchFilm();
  }, [filmId]);

  // Load past messages and setup real-time channel
  useEffect(() => {
    if (!film || !film.id) return;

    let isMounted = true;

    const fetchMessages = async () => {
      const { data, error: fetchError } = await supabase
        .from("messages")
        .select("*")
        .eq("film_id", film.id)
        .order("created_at", { ascending: false })
        .limit(100);

      if (fetchError) {
        // console.error("Error fetching messages:", fetchError);
        return;
      }

      if (isMounted) {
        setMessages((data || []).reverse());
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
      }
    };

    fetchMessages();

    // Setup real-time channel with broadcast
    const ch = supabase.channel(`film-chat:${film.id}`, {
      config: { presence: { key: generateUserId() } },
    });

    // Real-time message listener (broadcast)
    ch.on("broadcast", { event: "message" }, (payload) => {
      const msg = payload.payload || payload;

      if (isMounted) {
        setMessages((prev) => {
          // Avoid duplicates
          if (prev.some((m) => m.created_at === msg.created_at && m.nickname === msg.nickname && m.content === msg.content)) {
            return prev;
          }
          const updated = [...prev, msg];
          setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
          return updated.slice(-200); // Keep last 200 messages
        });
      }
    });

    // Presence listener for online users count
    ch.on("presence", { event: "sync" }, () => {
      const state = ch.presenceState();
      setUsersCount(Object.keys(state).length);
    });

    ch.subscribe(async (status) => {
      if (status === "SUBSCRIBED") {
        await ch.track({ online_at: new Date().toISOString() });
      }
    });

    setChannel(ch);

    return () => {
      isMounted = false;
      if (ch) {
        ch.untrack();
        ch.unsubscribe();
      }
    };
  }, [film]);

  // Fetch user interactions
  useEffect(() => {
    if (!film || !authUser) return;

    const fetchInteractions = async () => {
      const { data } = await supabase.from("user_interactions").select("status").eq("user_id", authUser.id).eq("imdb_id", film.imdb_id);

      if (data) {
        const interactions = {
          watched: data.some((i) => i.status === "watched"),
          favorite: data.some((i) => i.status === "favorite"),
          will_watch: data.some((i) => i.status === "will_watch"),
        };
        setUserInteractions(interactions);
      }
    };

    fetchInteractions();
  }, [film, authUser]);

  useEffect(() => {
    if (!film) return;

    const fetchFilmParties = async () => {
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);

      const { data } = await supabase
        .from("watch_parties")
        .select("*")
        .eq("imdb_id", film.imdb_id)
        .eq("status", "upcoming")
        .gte("scheduled_time", todayStart.toISOString()) // include all of today
        .order("scheduled_time", { ascending: true })
        .limit(10);

      setFilmWatchParties(data || []);
    };

    fetchFilmParties();
  }, [film]);

  // Fetch currently watching status
  useEffect(() => {
    if (!film || !authUser) return;

    const fetchCurrentStatus = async () => {
      const { data } = await supabase
        .from("user_current_activities")
        .select("id")
        .eq("user_id", authUser.id)
        .eq("imdb_id", film.imdb_id)
        .maybeSingle();

      setIsCurrentlyWatching(!!data);
    };

    fetchCurrentStatus();
  }, [film, authUser]);

  // Toggle currently watching
  const toggleCurrentlyWatching = async () => {
    if (!authUser) {
      setShowLoginModal(true);
      return;
    }
    if (!film || interactionLoading) return;

    setInteractionLoading(true);

    try {
      if (isCurrentlyWatching) {
        // Remove from currently watching
        const { error } = await supabase.from("user_current_activities").delete().eq("user_id", authUser.id).eq("imdb_id", film.imdb_id);

        if (error) throw error;
        setIsCurrentlyWatching(false);
      } else {
        // Add to currently watching
        const { error } = await supabase.from("user_current_activities").insert([
          {
            user_id: authUser.id,
            imdb_id: film.imdb_id,
            type: film.type,
          },
        ]);

        if (error) throw error;
        setIsCurrentlyWatching(true);
      }
    } catch (err) {
      // console.error("Currently watching error:", err);
      alert("Failed to update. Please try again.");
    }

    setInteractionLoading(false);
  };

  // Toggle interaction
  const toggleInteraction = async (status: "watched" | "favorite" | "will_watch") => {
    if (!authUser) {
      setShowLoginModal(true);
      return;
    }
    if (!film || interactionLoading) return;

    setInteractionLoading(true);

    try {
      const isActive = userInteractions[status];

      if (isActive) {
        // Remove interaction
        const { error } = await supabase
          .from("user_interactions")
          .delete()
          .eq("user_id", authUser.id)
          .eq("imdb_id", film.imdb_id)
          .eq("status", status);

        if (error) throw error;

        setUserInteractions((prev) => ({ ...prev, [status]: false }));
      } else {
        // Add interaction
        const { error } = await supabase.from("user_interactions").insert([
          {
            user_id: authUser.id,
            imdb_id: film.imdb_id,
            status: status,
          },
        ]);

        if (error) throw error;

        setUserInteractions((prev) => ({ ...prev, [status]: true }));
      }
    } catch (err) {
      // console.error("Interaction error:", err);
      alert("Failed to update. Please try again.");
    }

    setInteractionLoading(false);
  };

  // Handle watch party actions
  const handleWatchPartiesClick = () => {
    // if (!authUser) {
    //   setShowLoginModal(true);
    //   return;
    // }
    setShowWatchPartiesModal(true);
  };

  const handleCreatePartyClick = () => {
    if (!authUser) {
      setShowLoginModal(true);
      return;
    }
    setShowCreatePartyModal(true);
  };

  const generateNickname = () => {
    const num = Math.floor(Math.random() * 1_000_000); // 0 → 999999
    const padded = String(num).padStart(6, "0");
    return `anon${padded}`;
  };

  function ensureNickname(): string {
    if (nickname) return nickname;

    const generated = generateNickname();
    dispatch(setNickname(generated));
    return generated;
  }

  async function sendMessage() {
    if (!input.trim() || sendingMessage || !film || !channel) return;

    setSendingMessage(true);
    const timestamp = new Date().toISOString();

    let messagePayload: any;

    // Use username/display_name for auth users, or generated nickname for anonymous
    const messageNickname = authUser ? authUser.username || authUser.display_name || "user" : nickname || ensureNickname();

    messagePayload = {
      content: input.trim(),
      user_id: authUser?.id || null,
      nickname: messageNickname,
      created_at: timestamp,
    };

    try {
      await channel.send({
        type: "broadcast",
        event: "message",
        payload: messagePayload,
      });

      const { error: insertError } = await supabase.from("messages").insert([
        {
          film_id: film.id,
          content: messagePayload.content,
          nickname: messagePayload.nickname,
          user_id: messagePayload.user_id,
        },
      ]);

      if (insertError) {
        // console.error("Error inserting message:", insertError);
        throw insertError;
      }

      setMessages((prev) => [...prev, messagePayload].slice(-200));
      setInput("");
    } catch (err) {
      // console.error("Send error:", err);
      alert("Failed to send message");
    } finally {
      setSendingMessage(false);
      inputRef.current?.focus();
    }
  }

  // Get thumbnail (always return a usable URL)
  const getThumbnail = () => {
    if (!film) return "https://placehold.co/120x160/1f2937/ffffff/png?text=Image+Not+Found";
    let url: string | undefined;
    if (film.thumbnails) {
      try {
        const thumbs = typeof film.thumbnails === "string" ? JSON.parse(film.thumbnails) : film.thumbnails;
        url = thumbs[1]?.url || thumbs[0]?.url || film.primary_image;
      } catch {
        url = film.primary_image;
      }
    } else {
      url = film.primary_image;
    }

    return url || "https://placehold.co/120x160/1f2937/ffffff/png?text=Image+Not+Found";
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-950">
        <div className="text-white text-xl">Loading film...</div>
      </div>
    );
  }

  if (error || !film) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-gray-950 text-white p-4">
        <h2 className="text-2xl mb-2">Film not found</h2>
        <p className="text-gray-400 mb-4">{error || "The requested film could not be found."}</p>
        <p className="text-sm text-gray-500 mb-4">IMDB ID: {filmId}</p>
        <button onClick={() => navigate("/")} className="px-6 py-3 bg-red-600 rounded-lg hover:bg-red-700 transition-colors">
          Go Back Home
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-gray-950 text-white h-screen sm:h-auto">
      {/* Fixed Header */}
      <div className="sticky top-0 z-10 bg-gray-900 border-b-2 border-red-600 p-4 shadow-lg">
        <div className="max-w-5xl mx-auto flex items-center gap-6 justify-between flex-wrap sm:flex-nowrap sm:gap-8">
          {/* Film Info */}
          <div className="flex items-center gap-4 flex-shrink-0 w-full sm:w-auto">
            <button onClick={() => navigate("/")} className="text-red-600 hover:text-red-500 transition-colors" aria-label="Go back">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <button
              onClick={() => setShowFilmModal(true)}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity text-left min-w-0"
            >
              <img
                src={getThumbnail()}
                alt={film.original_title}
                className="w-12 h-16 object-cover rounded border border-red-600"
                onError={(e) => {
                  const target = e.currentTarget as HTMLImageElement;
                  target.onerror = null;
                  target.src = "https://placehold.co/120x160/1f2937/ffffff/png?text=Image+Not+Found";
                }}
              />
              <div className="min-w-0">
                <h3 className="text-lg font-bold line-clamp-1 my-0 truncate">{film.original_title}</h3>
                <p className="text-sm text-gray-400 truncate">
                  {film.type === "tvSeries" ? "TV Series" : "Movie"} • {film.start_year}{" "}
                  {film.average_rating && <span>• ⭐{Number(film.average_rating).toFixed(1)}</span>}
                </p>
              </div>
            </button>
          </div>
          {/* Interaction Buttons - Responsive and minimalistic, with visible icons */}
          <div className="flex items-center gap-2 sm:gap-3 ml-2  sm:mt-0 flex-wrap">
            <button
              onClick={toggleCurrentlyWatching}
              disabled={interactionLoading}
              title={isCurrentlyWatching ? "Currently Watching" : "Set as Currently Watching"}
              aria-label={isCurrentlyWatching ? "Currently Watching" : "Set Watching"}
              className={`group relative p-2 rounded-full border border-cyan-600 focus:outline-none focus:ring-2 focus:ring-cyan-400 bg-opacity-0 hover:bg-cyan-700/10 ${
                authUser && isCurrentlyWatching ? "bg-cyan-700/40" : "bg-transparent"
              }`}
            >
              <img
                src={CurrentIcon}
                className={`w-5 h-5 transition ${authUser && isCurrentlyWatching ? "brightness-200 invert" : "opacity-80"}`}
                alt="set-current-icon"
              />
              <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-black/80 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity">
                {isCurrentlyWatching ? "Currently watching" : "Set as currently watching"}
              </span>
            </button>

            {/* WATCHED */}
            <button
              onClick={() => toggleInteraction("watched")}
              disabled={interactionLoading}
              title={userInteractions.watched ? "Marked as Watched" : "Mark as Watched"}
              aria-label={userInteractions.watched ? "Marked as Watched" : "Mark as Watched"}
              className={`group relative p-2 rounded-full border border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-opacity-0 hover:bg-emerald-700/10 ${
                authUser && userInteractions.watched ? "bg-emerald-700/40" : "bg-transparent"
              }`}
            >
              <img
                src={CheckIcon}
                className={`w-5 h-5 transition ${authUser && userInteractions.watched ? "brightness-200 invert" : "opacity-80"}`}
                alt="set-watched-icon"
              />
              <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-black/80 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity">
                {userInteractions.watched ? "Marked as watched" : "Mark as watched"}
              </span>
            </button>

            {/* FAVORITE */}
            <button
              onClick={() => toggleInteraction("favorite")}
              disabled={interactionLoading}
              title={userInteractions.favorite ? "In Favorites" : "Add to Favorites"}
              aria-label={userInteractions.favorite ? "In Favorites" : "Add to Favorites"}
              className={`group relative p-2 rounded-full border border-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 bg-opacity-0 hover:bg-red-700/10 ${
                authUser && userInteractions.favorite ? "bg-red-700/40" : "bg-transparent"
              }`}
            >
              <img
                src={FavoriteIcon}
                className={`w-5 h-5 transition ${authUser && userInteractions.favorite ? "brightness-200 invert" : "opacity-80"}`}
                alt="set-fav-icon"
              />
              <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-black/80 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity">
                {userInteractions.favorite ? "In favorites" : "Add to favorites"}
              </span>
            </button>

            {/* WATCHLIST */}
            <button
              onClick={() => toggleInteraction("will_watch")}
              disabled={interactionLoading}
              title={userInteractions.will_watch ? "In Watchlist" : "Add to Watchlist"}
              aria-label={userInteractions.will_watch ? "In Watchlist" : "Add to Watchlist"}
              className={`group relative p-2 rounded-full border border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-opacity-0 hover:bg-blue-700/10 ${
                authUser && userInteractions.will_watch ? "bg-blue-700/40" : "bg-transparent"
              }`}
            >
              <img
                src={PlaylistIcon}
                className={`w-5 h-5 transition ${authUser && userInteractions.will_watch ? "brightness-200 invert" : "opacity-80"}`}
                alt="watch-list-icon"
              />
              <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-black/80 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity">
                {userInteractions.will_watch ? "In watchlist" : "Add to watchlist"}
              </span>
            </button>
            {/* Watch Parties / Create Watch Party */}
            <button
              onClick={handleCreatePartyClick}
              title="Create Watch Party"
              aria-label="Create Watch Party"
              className="group relative p-2 rounded-full border border-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-400 bg-purple-700/40 text-purple-400 hover:bg-purple-700 transition"
            >
              <span className="text-lg leading-none">🎉</span>
              <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-black/80 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity">
                Create watch party
              </span>
            </button>
            <button
              onClick={handleWatchPartiesClick}
              title="Watch Parties"
              aria-label="Watch Parties"
              className="group relative p-2 rounded-full border border-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-400 bg-rose-700/40 text-rose-400 hover:bg-rose-700 transition"
            >
              <span className="block sm:hidden text-base font-semibold">Parties</span>
              <span className="hidden sm:inline text-lg leading-none px-1">🎉 Watch Parties</span>
              <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-black/80 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity">
                View watch parties
              </span>
            </button>
          </div>
          {/* User Info */}
          <div className="flex flex-row items-center justify-end text-gray-400 ml-2 space-x-2 sm:flex-col sm:items-end sm:space-x-0 sm:text-right">
            <span className="font-normal text-xs sm:text-sm whitespace-nowrap">Chatting as</span>

            <span className="font-semibold text-red-500 truncate max-w-[120px] text-sm sm:text-base sm:max-w-[160px]">
              {authUser ? authUser.display_name ?? "You" : nickname || "Anonymous"}
            </span>

            {usersCount > 0 && <span className="text-xs text-green-400 whitespace-nowrap sm:mt-0.5">👥 {usersCount} online</span>}
          </div>
        </div>
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-4 bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 flex flex-col-reverse custom-scrollbar">
        <div className="max-w-5xl mx-auto w-full space-y-2 flex flex-col">
          <div ref={messagesEndRef} />
          {messages.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <p className="text-lg">No messages yet. Be the first to start the conversation!</p>
            </div>
          ) : (
            messages.map((m) => <ChatMessage key={m.id || `${m.created_at}-${m.nickname}`} message={m} />)
          )}
        </div>
      </div>

      {/* Fixed Input Footer */}
      <div className="sticky bottom-0 bg-gray-900 border-t-2 border-red-600 p-4 shadow-lg">
        <div className="max-w-5xl mx-auto flex gap-3 items-center">
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder="Type your message..."
            className="flex-1 px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 focus:border-red-600 focus:outline-none text-white placeholder-gray-500"
            disabled={sendingMessage}
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || sendingMessage}
            className="p-3 rounded-lg bg-red-600 hover:bg-red-700 disabled:bg-gray-700 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
          >
            <img src={sendIcon} alt="Send" className="w-5 h-5 invert brightness-200" />
          </button>
        </div>
      </div>

      {showCreatePartyModal && film && (
        <CreateWatchPartyModal
          preSelectedFilm={{
            imdb_id: film.imdb_id,
            title: film.original_title || "",
            type: film.type || "movie",
            primary_image: getThumbnail(), // Add this
          }}
          onClose={() => setShowCreatePartyModal(false)}
          onSuccess={() => {}}
        />
      )}

      {/* Watch Parties Modal */}
      {film && (
        <WatchPartiesModal
          isOpen={showWatchPartiesModal}
          onClose={() => setShowWatchPartiesModal(false)}
          filmTitle={film.original_title || "Unknown Film"}
          watchParties={filmWatchParties}
          onCreateParty={() => setShowCreatePartyModal(true)}
        />
      )}

      {/* Login Required Modal */}
      <LoginRequiredModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />

      {/* Film Info Modal */}
      {film && <FilmModal film={film} isOpen={showFilmModal} onClose={() => setShowFilmModal(false)} />}
    </div>
  );
};

export default ChatRoom;
